/**
 * Pagination dots for a native scroll-snap strip.
 *
 * The strip scrolls on its own (swipe, trackpad, keyboard). The dots follow
 * whichever slide leads the visible area, and a dot scrolls its slide to that
 * position. Without JavaScript the strip still scrolls and only the dots are
 * inert.
 *
 * Markup: <sensia-carousel> holding a [data-carousel-track] whose children are
 * the slides, and [data-carousel-dot] buttons, one per slide.
 */
class SensiaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.slides = this.track ? Array.from(this.track.children) : [];

    if (!this.track || this.slides.length < 2) return;

    this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDot));
    this.track?.removeEventListener('scroll', this.onScroll);
    cancelAnimationFrame(this.frame);
  }

  onDot = (event) => {
    const slide = this.slides[this.dots.indexOf(event.currentTarget)];
    if (!slide) return;

    // The strip's own scroll, not scrollIntoView, which would move the page too.
    this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft });
  };

  onScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.#update());
  };

  // The current slide is the one whose leading edge is nearest the strip's
  // snap position, which is where the first slide starts.
  #update() {
    const origin = this.slides[0].offsetLeft;
    const position = this.track.scrollLeft;
    let current = 0;
    let best = Infinity;

    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - origin - position);
      if (distance < best) {
        best = distance;
        current = i;
      }
    });

    // At the far end the last slides cannot reach the snap position, so the
    // end of the strip counts as the last slide.
    if (position + this.track.clientWidth >= this.track.scrollWidth - 2) current = this.slides.length - 1;

    this.dots.forEach((dot, i) => {
      if (i === current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('sensia-carousel')) {
  customElements.define('sensia-carousel', SensiaCarousel);
}
